import React, { useState, useEffect, useId, useMemo } from 'react';
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Typography, Box } from '@mui/material';
import { FONT_COLOR } from '../const/general'
import Progress from '../item/progress'

export default function BarLine(props) {
    const [categoryDates, setCategoryDates] = useState([]);
    const [xData, setXData] = useState([]);
    const [yData, setYData] = useState([]);
    const [seriesMax, setSeriesMax] = useState(0);
    const [riskPoints, setRiskPoints] = useState([]);
    const [isNoData, setIsNoData] = useState(false);

    // チャートごとの一意のキー
    const chartId = useId();

    const aryMax = function (a, b) { return Math.max(a, b); }

    // チャート情報を格納
    const setChartData = (date_ary, data_x, data_y, data_x2) => {
        // 配列を必ず新しい参照でセットして、同一参照による state 更新無視を防ぐ
        setCategoryDates(Array.isArray(date_ary) ? [...date_ary] : []);
        setXData(Array.isArray(data_x) ? [...data_x] : []);
        setYData(Array.isArray(data_y) ? [...data_y] : []);

        if (Array.isArray(data_x) && data_x.length > 0) {
            setSeriesMax(props.limitVal + 1);
            const maxVal = data_x.reduce(aryMax);
            if (maxVal > props.limitVal) {
                setSeriesMax(maxVal);
            }
            const points = props.getPoints(date_ary, data_x, data_x2);
            setRiskPoints(Array.isArray(points) ? [...points] : []);
        }
    }

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    // データ更新が完了したらローディング終了
    useEffect(() => {
        if (yData !== null) {
            const timer = setTimeout(() => {
                loadingEnd();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [yData]);

    useEffect(() => {
        if (props.dateArray === null
            || props.xDatetime === null
            || props.yDatetime === null)
        {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0
            && props.xDatetime && props.xDatetime.length > 0
            && props.yDatetime && props.yDatetime.length > 0
        ) {
            const optionalX2 = (props.xDatetime2 !== undefined) ? props.xDatetime2 : undefined;
            setChartData(props.dateArray, props.xDatetime, props.yDatetime, optionalX2)
        }
        setIsNoData(false)
    }, [props.dateArray, props.xDatetime, props.yDatetime]);

    useEffect(() => {
        if (props.dateArray === null ||
            props.xDatetime === null || 
            props.yDatetime === null
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0 &&
            props.xDatetime && props.xDatetime.length > 0 &&
            props.yDatetime && props.yDatetime.length > 0
        ) {
            const optionalX2 = (props.xDatetime2 !== undefined) ? props.xDatetime2 : undefined;
            setChartData(props.dateArray, props.xDatetime, props.yDatetime, optionalX2)
        }
        setIsNoData(false)

        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    const series = useMemo(() => {
        return [
            {
                name: props.seriesNameX,
                type: 'line',
                data: xData
            },
            {
                name: props.seriesNameY,
                type: 'column',
                data: yData
            },
        ];
    }, [xData, yData]);

    const normalizedYAnnotations = useMemo(() => {
        if (!props.annotations) {
            return [];
        }

        if (Array.isArray(props.annotations)) {
            return props.annotations.filter(Boolean);
        }

        if (typeof props.annotations === 'object' && Array.isArray(props.annotations.yaxis)) {
            return props.annotations.yaxis.filter(Boolean);
        }

        if (typeof props.annotations === 'object') {
            return [props.annotations];
        }

        return [];
    }, [props.annotations]);

    const options = useMemo(() => ({
        chart: {
            id: chartId,
            toolbar: {
                show: false,
                offsetY: 0
            },
            type: "line",
            stacked: false
        },
        // ポイントの数値表示
        dataLabels: {
            enabled: true,
            enabledOnSeries: [0, 1],
            offsetY: props.dataLabelsOffsetY ?? -10,
            style: {
                fontSize: '8px',
                colors: ['white', 'white']
            }
        },
        colors: [
            function ({ value, seriesIndex, dataPointIndex, w }) {
                if (seriesIndex === 1 && props.getBarColor) {
                    return props.getBarColor(value, w.config.series, seriesIndex, dataPointIndex, w);
                }
                if (Array.isArray(props.colors) && props.colors[seriesIndex]) {
                    return props.colors[seriesIndex];
                }
                return '#5c9fed';
            }
        ],
        stroke: {
            width: [2, 0],
            colors: props.strokeColors
        },
        annotations: {
            yaxis: normalizedYAnnotations,
            xaxis: [
                {
                    x: categoryDates[props.chooseNum],
                    borderColor: '#0000FF',
                    borderWidth: 1,
                    strokeDashArray: 4,
                    label: {
                        show: false
                    }
                }
            ],
            points: riskPoints
        },
        plotOptions: {
            bar: {
                dataLabels: {
                    position: 'top' // top, center, bottom
                },
                borderRadiusApplication: 'around'
            }
        },
        xaxis: {
            categories: categoryDates,
            labels: {
                rotate: -55,
                rotateAlways: true,
                style: {
                    colors: FONT_COLOR,
                    fontSize: '10px'
                }
            }
        },
        yaxis: [
            {
                opposite: true,
                min: props.minY ? props.minY : seriesMax * -1,
                max: seriesMax,
                seriesName: props.seriesNameX,
                axisTicks: {
                    show: false
                },
                axisBorder: {
                    show: false
                },
                labels: {
                    show: false
                },
                lines: {
                    show: false
                }
            },
            {
                min: 0,
                ...(props.maxY !== undefined ? { max: props.maxY } : {}),
                seriesName: props.seriesNameY,
                axisTicks: {
                    show: false
                },
                axisBorder: {
                    show: false
                },
                labels: {
                    show: false
                },
                lines: {
                    show: false
                }
            }
        ],
        tooltip: {
            shared: props.shared,
            intersect: props.intersect,
            x: {
                show: false
            },
            y: {
                formatter: props.format
            },
            // getTooltipMarkerColor が渡されたときは、マーカーの色を時刻ごとに変えるため自前で描画する
            ...(props.getTooltipMarkerColor && {
                custom: ({ series, dataPointIndex, w }) => {
                    const color = props.getTooltipMarkerColor(dataPointIndex, w);
                    return w.config.series.map((s, i) => {
                        const val = series[i][dataPointIndex];
                        const text = props.format ? props.format(val, { seriesIndex: i, dataPointIndex, w }) : val;
                        return `<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: ${i + 1}; display: flex;">`
                            + `<span class="apexcharts-tooltip-marker" style="background-color: ${color};"></span>`
                            + `<div class="apexcharts-tooltip-text"><div class="apexcharts-tooltip-y-group">`
                            + `<span class="apexcharts-tooltip-text-y-label">${s.name}: </span>`
                            + `<span class="apexcharts-tooltip-text-y-value">${text}</span>`
                            + `</div></div></div>`;
                    }).join('');
                }
            })
        },
        grid: {
            show: false, // 全体の罫線を消す
            padding: {
                left: 25,
                right: 2,
                top: -10,
                bottom: props.bottom
            }
        },
        legend: {
            show: false,
            height: 0,
        }
    }), [riskPoints, categoryDates, seriesMax, props.chooseNum, normalizedYAnnotations, props.colors, props.getBarColor, props.getTooltipMarkerColor]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (series && series.length > 0) {
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [series]);

    return (
        <>
            <Box
                sx={{
                    height: props.height,
                }}
            >
                {!props.isReadyFlg ?
                    <Progress />
                    :
                    isNoData ?
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                color: 'white',
                            }}
                        >
                            対象データがありません
                        </Typography>
                        :
                        <Chart
                            options={options}
                            series={series}
                            type="bar"
                            height="100%"
                            width="100%"
                            style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                        />
                }
            </Box>
        </>
    );
}